import { ErrorBoundary } from "react-error-boundary";
import ReactDOM from 'react-dom/client';
import './globals.css';

import { Router, Route, Switch } from 'wouter';
import { App as OwnerApp } from './owner/App.tsx';
import { WelcomePortal } from './pages/hub/WelcomePortal.tsx';

import MainShell from './layout/MainShell.tsx';
import { AppErrorFallback } from './components/ErrorFallback.tsx';
import { logoutWatcher, useIsAuthenticated } from './hooks/auth.ts';
import AuthShell from './layout/AuthShell.tsx';
import { isValidRedirectPath, redirectToLogin } from '@/lib/navigation';
import ThemeProvider from './components/ThemeProvider.tsx';
import { StrictMode, useEffect, useState } from 'react';
import { isMobile } from 'is-mobile';
import { useAtomValue, useSetAtom } from 'jotai';
import { pageTitleWatcher } from './hooks/pages.ts';
import { writeSecretAtom, writeSessionAtom } from './owner/atoms/auth';
import { useSetAuthData } from './hooks/auth.ts';
import { txToast } from '@/components/TxToaster';

// ── Global Network Fetch Interceptor for Mock Mode (Local Dev) ────────────────
if (import.meta.env.DEV) {
    const originalFetch = window.fetch;
    let mockDetailedPlayers: Record<string, any> = {
        'abcd123456efgh': {
            displayName: 'aisc0pez',
            pureName: 'aisc0pez',
            isRegistered: true,
            isConnected: true,
            netid: 12,
            sessionTime: 45,
            playTime: 520,
            tsJoined: Math.floor(Date.now() / 1000 - 86400 * 30),
            tsWhitelisted: Math.floor(Date.now() / 1000 - 86400 * 29),
            license: 'abcd123456efgh',
            ids: [
                'license:abcd123456efgh',
                'discord:123456789012345678',
                'steam:11000010c0ffee1',
                'ip:127.0.0.1'
            ],
            hwids: [
                'hwid:9a8b7c6d5e4f3a2b1c'
            ],
            oldIds: [
                'license:abcd123456efgh',
                'discord:123456789012345678',
                'steam:11000010c0ffee1'
            ],
            oldHwids: [
                'hwid:9a8b7c6d5e4f3a2b1c'
            ],
            notes: 'Owner of the server. Always helpful.',
            notesLog: 'Saved by dev_sc0pez',
            actionHistory: [
                { id: 'W-0001', type: 'warn', author: 'Joker', reason: 'Spawning cars in spawn area during event', ts: Math.floor(Date.now() / 1000 - 86400 * 15) }
            ],
            profilePicture: 'https://api.dicebear.com/7.x/identicon/svg?seed=aisc0pez',
            isAdmin: true
        },
        'bcde123456fghi': {
            displayName: 'Joker 🃏',
            pureName: 'Joker',
            isRegistered: true,
            isConnected: true,
            netid: 42,
            sessionTime: 12,
            playTime: 340,
            tsJoined: Math.floor(Date.now() / 1000 - 86400 * 25),
            tsWhitelisted: Math.floor(Date.now() / 1000 - 86400 * 24),
            license: 'bcde123456fghi',
            ids: [
                'license:bcde123456fghi',
                'discord:987654321098765432',
                'steam:110000102030405'
            ],
            hwids: [],
            oldIds: [],
            oldHwids: [],
            notes: 'Co-Owner.',
            notesLog: 'Saved by System',
            actionHistory: [],
            profilePicture: 'https://api.dicebear.com/7.x/pixel-art/svg?seed=joker',
            isAdmin: true
        },
        'cdef123456ghij': {
            displayName: 'NovaKnight 🛡️',
            pureName: 'NovaKnight',
            isRegistered: true,
            isConnected: false,
            playTime: 120,
            tsJoined: Math.floor(Date.now() / 1000 - 86400 * 15),
            tsLastConnection: Math.floor(Date.now() / 1000 - 3600),
            license: 'cdef123456ghij',
            ids: ['license:cdef123456ghij', 'discord:111111111111111111'],
            hwids: [],
            actionHistory: [],
            profilePicture: 'https://api.dicebear.com/7.x/bottts/svg?seed=novaknight',
            isAdmin: false
        },
        'defg123456hijk': {
            displayName: 'ToxicFlame',
            pureName: 'ToxicFlame',
            isRegistered: true,
            isConnected: false,
            playTime: 85,
            tsJoined: Math.floor(Date.now() / 1000 - 86400 * 10),
            tsLastConnection: Math.floor(Date.now() / 1000 - 3600 * 5),
            license: 'defg123456hijk',
            ids: ['license:defg123456hijk'],
            hwids: [],
            notes: 'Warned once for RDM.',
            notesLog: 'Saved by dev_sc0pez',
            actionHistory: [
                { id: 'W-0002', type: 'warn', author: 'dev_sc0pez', reason: 'RDM / VDM in safezone', ts: Math.floor(Date.now() / 1000 - 86400 * 5) }
            ],
            profilePicture: 'https://api.dicebear.com/7.x/adventurer/svg?seed=toxicflame',
            isAdmin: false
        },
        'efgh123456ijkl': {
            displayName: 'GhostRider99',
            pureName: 'GhostRider99',
            isRegistered: true,
            isConnected: false,
            playTime: 200,
            tsJoined: Math.floor(Date.now() / 1000 - 86400 * 7),
            tsLastConnection: Math.floor(Date.now() / 1000 - 86400),
            license: 'efgh123456ijkl',
            ids: ['license:efgh123456ijkl'],
            hwids: [],
            actionHistory: [],
            profilePicture: 'https://api.dicebear.com/7.x/pixel-art/svg?seed=ghostrider',
            isAdmin: false
        },
        'fghi123456jklm': {
            displayName: 'SilverLynx',
            pureName: 'SilverLynx',
            isRegistered: true,
            isConnected: true,
            netid: 112,
            sessionTime: 5,
            playTime: 45,
            tsJoined: Math.floor(Date.now() / 1000 - 86400 * 5),
            tsWhitelisted: Math.floor(Date.now() / 1000 - 86400 * 4),
            license: 'fghi123456jklm',
            ids: ['license:fghi123456jklm'],
            hwids: [],
            actionHistory: [],
            profilePicture: 'https://api.dicebear.com/7.x/bottts/svg?seed=silverlynx',
            isAdmin: false
        },
        'ghij123456klno': {
            displayName: 'CrimsonByte',
            pureName: 'CrimsonByte',
            isRegistered: true,
            isConnected: false,
            playTime: 12,
            tsJoined: Math.floor(Date.now() / 1000 - 86400 * 4),
            tsLastConnection: Math.floor(Date.now() / 1000 - 86400 * 2),
            license: 'ghij123456klno',
            ids: ['license:ghij123456klno'],
            hwids: [],
            actionHistory: [],
            profilePicture: 'https://api.dicebear.com/7.x/identicon/svg?seed=crimsonbyte',
            isAdmin: false
        },
        'hijk123456lmop': {
            displayName: 'NeonPulse ⚡',
            pureName: 'NeonPulse',
            isRegistered: true,
            isConnected: true,
            netid: 8,
            sessionTime: 65,
            playTime: 190,
            tsJoined: Math.floor(Date.now() / 1000 - 86400 * 3),
            tsWhitelisted: Math.floor(Date.now() / 1000 - 86400 * 2),
            license: 'hijk123456lmop',
            ids: ['license:hijk123456lmop'],
            hwids: [],
            notes: 'Moderator.',
            notesLog: 'Saved by dev_sc0pez',
            actionHistory: [],
            profilePicture: 'https://api.dicebear.com/7.x/adventurer/svg?seed=neonpulse',
            isAdmin: true
        }
    };

    let mockJobState = {
        active: false,
        progress: 0,
        status: 'idle' as 'idle' | 'starting' | 'running' | 'success' | 'failed' | 'cancelled',
        filesCount: 0,
        filesOptimized: 0,
        savedSize: 0,
        estimatedTimeRemaining: 0,
        error: null as string | null,
        logs: [] as string[]
    };
    window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
        const urlStr = typeof input === 'string' ? input : (input instanceof URL ? input.toString() : input.url);
        console.log('[MOCK FETCH]', urlStr);

        const jsonResponse = (data: any) => new Response(JSON.stringify(data), {
            status: 200,
            headers: { 'Content-Type': 'application/json; charset=UTF-8' }
        });

        // ── Owner Panel Mock Endpoints (Local Dev) ────────────────
        if (urlStr.includes('/panel/data')) {
            const MOCK = await import('./owner/lib/mock');
            return jsonResponse({
                session_token: 'mock-session-dev',
                system_cards: MOCK.MOCK_SYSTEM_CARDS,
                guilds: [{ id: '123456789', name: 'SC Labs', member_count: 420 }],
                selected_guild: { id: '123456789', name: 'SC Labs', member_count: 420 },
                uptime: '2d 14h 32m',
            });
        }

        if (urlStr.includes('/panel/stats')) {
            const MOCK = await import('./owner/lib/mock');
            return jsonResponse({ system_cards: MOCK.MOCK_SYSTEM_CARDS });
        }

        if (urlStr.includes('/panel/access')) {
            const MOCK = await import('./owner/lib/mock');
            let action = undefined;
            if (init?.body) {
                try {
                    const parsedBody = JSON.parse(init.body as string);
                    action = parsedBody.action;
                } catch (_) {}
            }
            if (action === 'add' || action === 'delete') {
                return jsonResponse({ ok: true, message: `Simulated ${action}`, access_config: MOCK.MOCK_ACCESS_CONFIG });
            }
            return jsonResponse({ access_config: MOCK.MOCK_ACCESS_CONFIG });
        }

        if (urlStr.includes('/panel/lavalink')) {
            const MOCK = await import('./owner/lib/mock');
            return jsonResponse(MOCK.MOCK_LAVALINK);
        }

        if (urlStr.includes('/panel/backroom/fivem-config')) {
            const MOCK = await import('./owner/lib/mock');
            let action = undefined;
            if (init?.body) {
                try {
                    const parsedBody = JSON.parse(init.body as string);
                    action = parsedBody.action;
                } catch (_) {}
            }
            if (action === 'set') return jsonResponse({ ok: true, message: 'FiveM settings saved (mock).' });
            return jsonResponse(MOCK.MOCK_FIVEM_CONFIG);
        }

        if (urlStr.includes('/panel/backroom/fivem-users')) {
            const MOCK = await import('./owner/lib/mock');
            let action = undefined;
            if (init?.body) {
                try {
                    const parsedBody = JSON.parse(init.body as string);
                    action = parsedBody.action;
                } catch (_) {}
            }
            if (action === 'create') return jsonResponse({ ok: true, message: 'User created (mock).', temp_password: 'MOCK-TMP-PW' });
            if (action === 'delete') return jsonResponse({ ok: true, message: 'User deleted (mock).' });
            return jsonResponse({ users: MOCK.MOCK_FIVEM_USERS });
        }

        if (urlStr.includes('/panel/backroom/logs')) {
            const MOCK = await import('./owner/lib/mock');
            let offset = 0;
            if (init?.body) {
                try {
                    const parsedBody = JSON.parse(init.body as string);
                    offset = parsedBody.offset || 0;
                } catch (_) {}
            }
            const lines = offset === 0 ? MOCK.MOCK_LOG_LINES : [];
            return jsonResponse({ lines, next_offset: MOCK.MOCK_LOG_LINES.join('\n').length });
        }

        if (urlStr.includes('/panel/backroom/cmd')) {
            let cmd = '';
            if (init?.body) {
                try {
                    const parsedBody = JSON.parse(init.body as string);
                    cmd = parsedBody.cmd || '';
                } catch (_) {}
            }
            return jsonResponse({ ok: true, message: `[mock] Executed: ${cmd}` });
        }

        if (urlStr.includes('/panel/control')) {
            let action = '';
            if (init?.body) {
                try {
                    const parsedBody = JSON.parse(init.body as string);
                    action = parsedBody.action || '';
                } catch (_) {}
            }
            return jsonResponse({ ok: true, message: `[mock] Control: ${action}` });
        }

        // 1. Password verification
        if (urlStr.includes('/auth/login')) {
            return jsonResponse({
                name: 'sc0pez',
                permissions: ['all_permissions'],
                isMaster: true,
                isTempPassword: false,
                profilePicture: undefined,
                csrfToken: 'dev-csrf-token',
            });
        }

        // 2. Audit trail
        if (urlStr.includes('/fivem/audit')) {
            const { MOCK_AUDIT } = await import('./lib/mock');
            return jsonResponse({ rows: MOCK_AUDIT });
        }

        // 3. SFTP connector info
        if (urlStr.includes('/sftp/info')) {
            return jsonResponse({
                sftp: {
                    user: 'sclabs_operator',
                    host: 'sftp.sc-labs.xyz',
                    port: 2222,
                    path: '/home/fivem/server'
                }
            });
        }

        // 4. Configs
        if (urlStr.includes('/settings/configs')) {
            const { MOCK_CONFIGS } = await import('./lib/mock');
            return jsonResponse(MOCK_CONFIGS);
        }

        // 5. Save config card endpoint
        if (urlStr.includes('/settings/configs/')) {
            const { MOCK_CONFIGS } = await import('./lib/mock');
            return jsonResponse({
                success: true,
                stored: MOCK_CONFIGS.storedConfigs,
                changelog: MOCK_CONFIGS.changelog,
            });
        }

        // 6. Ban Templates
        if (urlStr.includes('/settings/banTemplates') || urlStr.includes('/settings/ban-templates')) {
            const { MOCK_BAN_TEMPLATES } = await import('./lib/mock');
            return jsonResponse({ templates: MOCK_BAN_TEMPLATES });
        }

        // 7. Whitelist
        if (urlStr.includes('/whitelist')) {
            const { MOCK_WHITELIST } = await import('./lib/mock');
            return jsonResponse({ rows: MOCK_WHITELIST });
        }

        // 8. Logs (Console & Action Log)
        if (urlStr.includes('/systemLog/console')) {
            const { MOCK_CONSOLE_BUFFER } = await import('./lib/mock');
            return jsonResponse({ data: MOCK_CONSOLE_BUFFER });
        }
        if (urlStr.includes('/systemLog/action')) {
            const { MOCK_ACTION_BUFFER } = await import('./lib/mock');
            return jsonResponse({ data: MOCK_ACTION_BUFFER });
        }

        // 9. Performance chart data
        if (urlStr.includes('/perfChartData/')) {
            const { getMockPerfChartData } = await import('./lib/mock');
            return jsonResponse(getMockPerfChartData());
        }

        // 10. Player drops analytical chart data
        if (urlStr.includes('/playerDropsData')) {
            const { getMockPlayerDropsData } = await import('./lib/mock');
            return jsonResponse(getMockPlayerDropsData());
        }

        // 11. Admins (simulating users endpoint)
        if (urlStr.includes('/users') || urlStr.includes('/admins')) {
            const { MOCK_ADMINS } = await import('./lib/mock');
            return jsonResponse({ users: MOCK_ADMINS });
        }

        // 12. File Manager (Directories & Actions)
        if (urlStr.includes('/files') || urlStr.includes('/fivem/files')) {
            const { MOCK_FILES_MAP } = await import('./lib/mock');
            const urlObj = new URL(urlStr, window.location.origin);
            const pathParam = urlObj.searchParams.get('path') || '';
            const normalizedPath = pathParam.replace(/^\/+|\/+$/g, '');

            if (urlStr.includes('/download')) {
                const lowerPath = normalizedPath.toLowerCase();
                const isSvg = lowerPath.endsWith('.svg');
                const isPng = lowerPath.endsWith('.png');
                const isJpg = lowerPath.endsWith('.jpg') || lowerPath.endsWith('.jpeg');
                const isWebp = lowerPath.endsWith('.webp');
                const isGif = lowerPath.endsWith('.gif');
                const isIco = lowerPath.endsWith('.ico');
                const isImg = isSvg || isPng || isJpg || isWebp || isGif || isIco;

                if (isImg) {
                    let svgContent = '';
                    if (lowerPath.endsWith('logo_preview.svg')) {
                        svgContent = `<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200" viewBox="0 0 200 200">
  <rect width="100%" height="100%" fill="#090c14" rx="20"/>
  <circle cx="100" cy="100" r="60" fill="none" stroke="#00ffd1" stroke-width="2" opacity="0.3"/>
  <circle cx="100" cy="100" r="50" fill="none" stroke="#a78bfa" stroke-width="1" opacity="0.2"/>
  <path d="M 50,100 Q 100,50 150,100 Q 100,150 50,100 Z" fill="none" stroke="#00ffd1" stroke-width="4"/>
  <circle cx="100" cy="100" r="25" fill="#a78bfa" opacity="0.8"/>
  <circle cx="100" cy="100" r="12" fill="#090c14"/>
  <circle cx="95" cy="95" r="4" fill="#ffffff"/>
  <text x="100" y="175" fill="#ffffff" font-family="sans-serif" font-size="12" text-anchor="middle" font-weight="bold" letter-spacing="2">SC LABS</text>
</svg>`;
                    } else if (lowerPath.endsWith('banner_sclabs.png')) {
                        svgContent = `<svg xmlns="http://www.w3.org/2000/svg" width="600" height="300" viewBox="0 0 600 300">
  <defs>
    <linearGradient id="bg" x1="0%" y1="0%" x2="100%" y2="100%">
      <stop offset="0%" stop-color="#0a0e17"/>
      <stop offset="100%" stop-color="#04060a"/>
    </linearGradient>
    <linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="0%">
      <stop offset="0%" stop-color="#00ffd1"/>
      <stop offset="100%" stop-color="#a78bfa"/>
    </linearGradient>
  </defs>
  <rect width="100%" height="100%" fill="url(#bg)"/>
  <path d="M 0,50 L 600,50 M 0,100 L 600,100 M 0,150 L 600,150 M 0,200 L 600,200 M 0,250 L 600,250" stroke="#ffffff" stroke-width="1" opacity="0.03"/>
  <path d="M 100,0 L 100,300 M 200,0 L 200,300 M 300,0 L 300,300 M 400,0 L 400,300 M 500,0 L 500,300" stroke="#ffffff" stroke-width="1" opacity="0.03"/>
  <circle cx="500" cy="150" r="120" fill="#a78bfa" opacity="0.08" filter="blur(20px)"/>
  <circle cx="100" cy="150" r="100" fill="#00ffd1" opacity="0.05" filter="blur(20px)"/>
  <text x="50" y="130" fill="url(#grad)" font-family="sans-serif" font-size="32" font-weight="900" letter-spacing="4">SC LABS PANEL</text>
  <text x="50" y="170" fill="#ffffff" font-family="sans-serif" font-size="16" font-weight="600" opacity="0.8">SYSTEM STATUS: ONLINE</text>
  <text x="50" y="200" fill="#475569" font-family="monospace" font-size="12">Host: 127.0.0.1 | Port: 40122 | Version: v2.0.0</text>
  <rect x="50" y="220" width="500" height="2" fill="url(#grad)" opacity="0.5"/>
</svg>`;
                    } else if (lowerPath.endsWith('avatar_default.jpg')) {
                        svgContent = `<svg xmlns="http://www.w3.org/2000/svg" width="150" height="150" viewBox="0 0 150 150">
  <rect width="100%" height="100%" fill="#111827"/>
  <circle cx="75" cy="75" r="50" fill="#374151"/>
  <circle cx="75" cy="60" r="20" fill="#9CA3AF"/>
  <path d="M 40,110 C 40,90 60,85 75,85 C 90,85 110,90 110,110 Z" fill="#9CA3AF"/>
  <circle cx="75" cy="75" r="70" fill="none" stroke="#a78bfa" stroke-width="2" opacity="0.4"/>
</svg>`;
                    } else if (lowerPath.endsWith('hud_preview.png')) {
                        svgContent = `<svg xmlns="http://www.w3.org/2000/svg" width="500" height="250" viewBox="0 0 500 250">
  <rect width="100%" height="100%" fill="#090c14"/>
  <rect x="30" y="30" width="440" height="190" rx="15" fill="#0c101c" stroke="#00ffd1" stroke-width="1" opacity="0.4"/>
  <text x="250" y="70" fill="#00ffd1" font-family="sans-serif" font-size="18" font-weight="bold" text-anchor="middle">SC LABS HUD</text>
  <circle cx="100" cy="140" r="25" fill="none" stroke="#ef4444" stroke-width="6"/>
  <text x="100" y="145" fill="#ef4444" font-family="monospace" font-size="12" text-anchor="middle" font-weight="bold">HP</text>
  <circle cx="180" cy="140" r="25" fill="none" stroke="#3b82f6" stroke-width="6"/>
  <text x="180" y="145" fill="#3b82f6" font-family="monospace" font-size="12" text-anchor="middle" font-weight="bold">ARM</text>
  <circle cx="260" cy="140" r="25" fill="none" stroke="#10b981" stroke-width="6"/>
  <text x="260" y="145" fill="#10b981" font-family="monospace" font-size="12" text-anchor="middle" font-weight="bold">FD</text>
  <circle cx="340" cy="140" r="25" fill="none" stroke="#f59e0b" stroke-width="6"/>
  <text x="340" y="145" fill="#f59e0b" font-family="monospace" font-size="12" text-anchor="middle" font-weight="bold">WTR</text>
  <rect x="400" y="115" width="50" height="50" rx="5" fill="#1e293b"/>
  <text x="425" y="145" fill="#ffffff" font-family="sans-serif" font-size="10" text-anchor="middle">🎤 60%</text>
</svg>`;
                    } else {
                        const filename = normalizedPath.split('/').pop() || 'image';
                        svgContent = `<svg xmlns="http://www.w3.org/2000/svg" width="300" height="200" viewBox="0 0 300 200">
  <rect width="100%" height="100%" fill="#070a12"/>
  <rect x="10" y="10" width="280" height="180" fill="none" stroke="#00ffd1" stroke-width="1" opacity="0.3"/>
  <text x="150" y="90" fill="#00ffd1" font-family="monospace" font-size="14" text-anchor="middle" font-weight="bold">${filename}</text>
  <text x="150" y="120" fill="#475569" font-family="sans-serif" font-size="11" text-anchor="middle">Mock Image File Preview</text>
</svg>`;
                    }

                    return new Response(svgContent, {
                        status: 200,
                        headers: { 'Content-Type': 'image/svg+xml' }
                    });
                }

                // Return mock file contents based on name
                let content = '# SC Labs Configuration File\n# Feel free to edit this locally!';
                if (normalizedPath.endsWith('.env')) {
                    content = 'PORT=40122\nDB_HOST=127.0.0.1\nDB_USER=root\nDB_PASS=cybersecret';
                } else if (normalizedPath.endsWith('README.md')) {
                    content = '# SC Labs\n\nWelcome to your FiveM control terminal. Explore options in the sidebar.';
                } else if (normalizedPath.endsWith('server.cfg')) {
                    content = 'ensure qb-core\nensure ox_lib\nensure sc-labs-hud\n\nsv_maxclients 64\nendpoint_add_tcp "0.0.0.0:30120"';
                } else if (normalizedPath.endsWith('.lua')) {
                    content = `-- SC Labs Script File: ${normalizedPath}\n\nCitizen.CreateThread(function()\n    while true do\n        Citizen.Wait(1000)\n        print("SC Labs HUD working perfectly...")\n    end\nend)`;
                } else if (normalizedPath.endsWith('.html')) {
                    content = `<!DOCTYPE html>\n<html>\n<head>\n    <title>HUD UI</title>\n</head>\n<body>\n    <h1>SC Labs Hud</h1>\n</body>\n</html>`;
                } else if (normalizedPath.endsWith('.css')) {
                    content = `/* HUD styles */\nbody {\n    background: rgba(0,0,0,0);\n    color: white;\n    font-family: monospace;\n}`;
                } else if (normalizedPath.endsWith('.js')) {
                    content = `// HUD JS script\nwindow.addEventListener('message', (event) => {\n    if (event.data.action === 'show') {\n        document.body.style.display = 'block';\n    }\n});`;
                }
                return new Response(content, {
                    status: 200,
                    headers: { 'Content-Type': 'text/plain; charset=UTF-8' }
                });
            }

            if (init?.method === 'POST') {
                return jsonResponse({ success: true, message: 'Simulated operation successful.' });
            }

            const files = MOCK_FILES_MAP[normalizedPath] || [];
            return jsonResponse({ entries: files });
        }

        // 13. Server control actions
        if (urlStr.includes('/server/action')) {
            const body = init?.body ? JSON.parse(init.body) : {};
            return jsonResponse({
                type: 'success',
                msg: `Simulated server ${body.action} command triggered.`
            });
        }

        // 14. History stats mock
        if (urlStr.includes('/history/stats')) {
            return jsonResponse({
                totalWarns: 42,
                warnsLast7d: 5,
                totalBans: 19,
                bansLast7d: 2,
                groupedByAdmins: [
                    { name: 'sc0pez', actions: 25 },
                    { name: 'Joker', actions: 12 },
                    { name: 'NeonPulse', actions: 4 },
                ]
            });
        }

        // 15. History search list mock
        if (urlStr.includes('/history/search')) {
            return jsonResponse({
                history: [
                    { id: 'A-2910', type: 'ban', playerName: 'aisc0pez', author: 'sc0pez', reason: 'Using hack menus', timestamp: Math.floor(Date.now() / 1000 - 3600), isRevoked: false, banExpiration: 'permanent' },
                    { id: 'A-5812', type: 'warn', playerName: 'ToxicFlame', author: 'Joker', reason: 'Fail RP / driving on tracks', timestamp: Math.floor(Date.now() / 1000 - 86400 * 2), isRevoked: false, warnAcked: true },
                    { id: 'A-1452', type: 'ban', playerName: 'GhostRider99', author: 'sc0pez', reason: 'RDM / killing players at spawn', timestamp: Math.floor(Date.now() / 1000 - 86400 * 5), isRevoked: false, banExpiration: 'active' },
                    { id: 'A-9012', type: 'warn', playerName: 'Joker', author: 'sc0pez', reason: 'Testing warn command', timestamp: Math.floor(Date.now() / 1000 - 86400 * 10), isRevoked: true },
                ],
                hasReachedEnd: true
            });
        }

        // 16. History action modal mock
        if (urlStr.includes('/history/action')) {
            const id = new URL(urlStr, 'http://x').searchParams.get('id') ?? 'A-2910';
            const isBan = !id.startsWith('A-5812') && !id.startsWith('A-9012');
            return jsonResponse({
                serverTime: Math.floor(Date.now() / 1000),
                action: {
                    id,
                    type: isBan ? 'ban' : 'warn',
                    playerName: isBan ? 'aisc0pez' : 'ToxicFlame',
                    author: 'sc0pez',
                    reason: isBan
                        ? 'Using hack menus and exploiting server economy. Caught on multiple occasions with visual proof from staff recording.'
                        : 'Fail RP / driving on train tracks during active RP scene.',
                    timestamp: Math.floor(Date.now() / 1000 - 3600),
                    expiration: isBan ? false : undefined,
                    ids: [
                        'license:a1b2c3d4e5f6a1b2c3d4e5f6a1b2c3d4e5f6a1b2',
                        'discord:492463078580158476',
                        'steam:1100001abc123',
                        'ip:96.126.188.46',
                    ],
                    hwids: [
                        'deadbeef1234567890abcdef1234567890abcdef12345678',
                    ],
                    revocation: { timestamp: null, author: null },
                    acked: !isBan ? true : undefined,
                },
            });
        }

        // 16b. Revoke action mock
        if (urlStr.includes('/history/revokeAction')) {
            return jsonResponse({ success: true });
        }

        // 17. Texture Optimizer Mock Endpoints
        if (urlStr.includes('/fivem/toolkitv/resources')) {
            return jsonResponse({
                resources: [
                    {
                        name: 'sc-labs-hud',
                        relativePath: 'sc-labs-hud',
                        fileCount: 4,
                        sizeMb: 52.4,
                        hasOversized: true,
                        oversizedFiles: [
                            { name: 'hud_preview.ytd', sizeMb: 24.5 },
                            { name: 'icons.ytd', sizeMb: 18.2 }
                        ],
                        ytdFiles: [
                            { name: 'hud_preview.ytd', sizeMb: 24.5 },
                            { name: 'icons.ytd', sizeMb: 18.2 },
                            { name: 'hud_atlas.ytd', sizeMb: 6.3 },
                            { name: 'hud_misc.ytd', sizeMb: 3.4 },
                        ]
                    },
                    {
                        name: 'qb-core',
                        relativePath: 'qb-core',
                        fileCount: 1,
                        sizeMb: 0.1,
                        hasOversized: false,
                        oversizedFiles: [],
                        ytdFiles: [
                            { name: 'qb_core.ytd', sizeMb: 0.1 },
                        ]
                    },
                    {
                        name: 'ox_lib',
                        relativePath: 'ox_lib',
                        fileCount: 0,
                        sizeMb: 0.0,
                        hasOversized: false,
                        oversizedFiles: [],
                        ytdFiles: []
                    },
                    {
                        name: 'custom-cars',
                        relativePath: 'custom-cars',
                        fileCount: 12,
                        sizeMb: 145.2,
                        hasOversized: true,
                        oversizedFiles: [
                            { name: 't20.ytd', sizeMb: 35.1 },
                            { name: 'zentorno.ytd', sizeMb: 22.8 }
                        ],
                        ytdFiles: [
                            { name: 't20.ytd', sizeMb: 35.1 },
                            { name: 'zentorno.ytd', sizeMb: 22.8 },
                            { name: 'vehicle_sports_01.ytd', sizeMb: 18.4 },
                            { name: 'vehicle_sports_02.ytd', sizeMb: 15.2 },
                            { name: 'vehicle_truck_01.ytd', sizeMb: 12.1 },
                            { name: 'vehicle_sedan_01.ytd', sizeMb: 8.3 },
                            { name: 'vehicle_suv_01.ytd', sizeMb: 7.6 },
                            { name: 'vehicle_bike_01.ytd', sizeMb: 6.4 },
                            { name: 'vehicle_van_01.ytd', sizeMb: 5.9 },
                            { name: 'vehicle_bus_01.ytd', sizeMb: 5.1 },
                            { name: 'vehicle_sedan_02.ytd', sizeMb: 4.8 },
                            { name: 'vehicle_sports_03.ytd', sizeMb: 4.2 },
                        ]
                    }
                ]
            });
        }

        if (urlStr.includes('/fivem/toolkitv/scan-logs')) {
            return jsonResponse({
                oversized: [
                    {
                        name: 'sc-labs-hud',
                        relativePath: 'sc-labs-hud',
                        ytdCount: 4,
                        oversizedFiles: [
                            { name: 'hud_preview.ytd', sizeMb: 24.5 },
                            { name: 'icons.ytd', sizeMb: 18.2 }
                        ]
                    },
                    {
                        name: 'custom-cars',
                        relativePath: 'custom-cars',
                        ytdCount: 12,
                        oversizedFiles: [
                            { name: 't20.ytd', sizeMb: 35.1 }
                        ]
                    }
                ]
            });
        }

        if (urlStr.includes('/fivem/toolkitv/progress')) {
            if (mockJobState.active) {
                mockJobState.progress += 20;
                mockJobState.estimatedTimeRemaining = Math.max(0, mockJobState.estimatedTimeRemaining - 1.5);
                if (mockJobState.progress >= 100) {
                    mockJobState.progress = 100;
                    mockJobState.active = false;
                    mockJobState.status = 'success';
                    mockJobState.filesOptimized = 4;
                    mockJobState.savedSize = 35.8;
                    mockJobState.logs.push(
                        'Optimizing hud_preview.ytd... Done (Saved 8.4MB)',
                        'Optimizing icons.ytd... Done (Saved 5.1MB)',
                        'Optimizing t20.ytd... Done (Saved 12.3MB)',
                        'Optimizing zentorno.ytd... Done (Saved 10.0MB)',
                        'Optimization job completed successfully!',
                        'Saved total of 35.8 MB!'
                    );
                } else {
                    mockJobState.filesOptimized = Math.floor((mockJobState.progress / 100) * 4);
                    mockJobState.logs.push(`Optimized file ${mockJobState.filesOptimized} of 4...`);
                }
            }
            return jsonResponse({ ...mockJobState, currentFile: null });
        }

        if (urlStr.includes('/fivem/toolkitv/optimize')) {
            let targetPath = 'ALL';
            if (init?.body) {
                try {
                    const body = JSON.parse(init.body as string);
                    targetPath = body.path || 'ALL';
                } catch (_) {}
            }
            mockJobState = {
                active: true,
                progress: 0,
                status: 'running',
                filesCount: 4,
                filesOptimized: 0,
                savedSize: 0,
                estimatedTimeRemaining: 6,
                error: null,
                logs: [
                    'Starting texture optimization job...',
                    `Target: /resources/${targetPath}`,
                    'Scanning files in resource directory...',
                    'Found 4 oversized .YTD files.'
                ]
            };
            return jsonResponse({ ok: true });
        }

        if (urlStr.includes('/fivem/toolkitv/cancel')) {
            mockJobState.active = false;
            mockJobState.status = 'cancelled';
            mockJobState.logs.push('Optimization cancelled by administrator.');
            return jsonResponse({ ok: true });
        }

        // 17. Resources Page Mock Endpoints
        if (urlStr.includes('/resources/')) {
            return jsonResponse({ success: true, message: 'Simulated operation successful.' });
        }
        if (urlStr.endsWith('/resources') || urlStr.includes('/resources?')) {
            const { MOCK_RESOURCES } = await import('./lib/mock');
            return jsonResponse({ resources: MOCK_RESOURCES });
        }

        // 18. Player Search & Stats Mock Endpoints
        if (urlStr.includes('/player/stats')) {
            return jsonResponse({
                total: Object.keys(mockDetailedPlayers).length,
                playedLast24h: 5,
                joinedLast24h: 1,
                joinedLast7d: 3
            });
        }

        // 18.1 Player Modal Detail API
        if (urlStr.includes('/player?') || urlStr.endsWith('/player')) {
            if (!urlStr.includes('/player/stats') && 
                !urlStr.includes('/player/search') && 
                !urlStr.includes('/player/save_note') && 
                !urlStr.includes('/player/whitelist') && 
                !urlStr.includes('/player/kick') && 
                !urlStr.includes('/player/warn') && 
                !urlStr.includes('/player/ban') && 
                !urlStr.includes('/player/message')) {
                
                const urlObj = new URL(urlStr, window.location.origin);
                const license = urlObj.searchParams.get('license');
                const netid = urlObj.searchParams.get('netid');
                
                let foundPlayer = null;
                if (license && mockDetailedPlayers[license]) {
                    foundPlayer = mockDetailedPlayers[license];
                } else if (netid) {
                    foundPlayer = Object.values(mockDetailedPlayers).find(p => p.netid?.toString() === netid.toString());
                }
                
                if (!foundPlayer) {
                    foundPlayer = {
                        displayName: license ? `Player_${license.substring(0, 6)}` : `Player_Offline`,
                        pureName: license ? `Player_${license.substring(0, 6)}` : `Player_Offline`,
                        isRegistered: false,
                        isConnected: false,
                        license: license || null,
                        ids: license ? [`license:${license}`] : [],
                        hwids: [],
                        actionHistory: []
                    };
                }
                
                return jsonResponse({
                    serverTime: Math.floor(Date.now() / 1000),
                    banTemplates: [
                        { name: 'Cheating', reason: 'Using hacks/exploits', duration: 'permanent' },
                        { name: 'Trolling', reason: 'Fail RP / Trolling', duration: '3 days' },
                        { name: 'RDM', reason: 'Random Deathmatch', duration: '24 hours' }
                    ],
                    player: foundPlayer
                });
            }
        }

        // 18.2 Save player note
        if (urlStr.includes('/player/save_note')) {
            const urlObj = new URL(urlStr, window.location.origin);
            const license = urlObj.searchParams.get('license');
            const netid = urlObj.searchParams.get('netid');
            
            let foundPlayer = null;
            if (license && mockDetailedPlayers[license]) {
                foundPlayer = mockDetailedPlayers[license];
            } else if (netid) {
                foundPlayer = Object.values(mockDetailedPlayers).find(p => p.netid?.toString() === netid.toString());
            }
            
            if (foundPlayer) {
                let noteContent = '';
                if (init?.body) {
                    try {
                        const parsedBody = JSON.parse(init.body as string);
                        noteContent = parsedBody.note || '';
                    } catch (_) {}
                }
                foundPlayer.notes = noteContent;
                foundPlayer.notesLog = `Saved by dev_sc0pez at ${new Date().toLocaleTimeString()}`;
            }
            return jsonResponse({ success: true });
        }

        // 18.3 Whitelist toggling
        if (urlStr.includes('/player/whitelist')) {
            const urlObj = new URL(urlStr, window.location.origin);
            const license = urlObj.searchParams.get('license');
            const netid = urlObj.searchParams.get('netid');
            
            let foundPlayer = null;
            if (license && mockDetailedPlayers[license]) {
                foundPlayer = mockDetailedPlayers[license];
            } else if (netid) {
                foundPlayer = Object.values(mockDetailedPlayers).find(p => p.netid?.toString() === netid.toString());
            }
            
            if (foundPlayer) {
                let status = false;
                if (init?.body) {
                    try {
                        const parsedBody = JSON.parse(init.body as string);
                        status = parsedBody.status || false;
                    } catch (_) {}
                }
                if (status === false) {
                    delete foundPlayer.tsWhitelisted;
                } else {
                    foundPlayer.tsWhitelisted = Math.floor(Date.now() / 1000);
                }
            }
            return jsonResponse({ success: true });
        }

        // 18.4 Warn Player
        if (urlStr.includes('/player/warn')) {
            const urlObj = new URL(urlStr, window.location.origin);
            const license = urlObj.searchParams.get('license');
            const netid = urlObj.searchParams.get('netid');
            
            let foundPlayer = null;
            if (license && mockDetailedPlayers[license]) {
                foundPlayer = mockDetailedPlayers[license];
            } else if (netid) {
                foundPlayer = Object.values(mockDetailedPlayers).find(p => p.netid?.toString() === netid.toString());
            }
            
            let reason = 'No reason provided';
            if (init?.body) {
                try {
                    const parsedBody = JSON.parse(init.body as string);
                    reason = parsedBody.reason || 'No reason provided';
                } catch (_) {}
            }
            
            if (foundPlayer) {
                foundPlayer.actionHistory = foundPlayer.actionHistory || [];
                foundPlayer.actionHistory.unshift({
                    id: `W-${Math.floor(Math.random() * 9000 + 1000)}`,
                    type: 'warn',
                    author: 'dev_sc0pez',
                    reason: reason,
                    ts: Math.floor(Date.now() / 1000)
                });
            }
            return jsonResponse({ type: 'success', msg: 'Player warned successfully!' });
        }

        // 18.5 Kick Player
        if (urlStr.includes('/player/kick')) {
            const urlObj = new URL(urlStr, window.location.origin);
            const license = urlObj.searchParams.get('license');
            const netid = urlObj.searchParams.get('netid');
            
            let foundPlayer = null;
            if (license && mockDetailedPlayers[license]) {
                foundPlayer = mockDetailedPlayers[license];
            } else if (netid) {
                foundPlayer = Object.values(mockDetailedPlayers).find(p => p.netid?.toString() === netid.toString());
            }
            
            let reason = 'No reason provided';
            if (init?.body) {
                try {
                    const parsedBody = JSON.parse(init.body as string);
                    reason = parsedBody.reason || 'No reason';
                } catch (_) {}
            }
            
            if (foundPlayer) {
                foundPlayer.isConnected = false;
                foundPlayer.netid = undefined;
            }
            return jsonResponse({ type: 'success', msg: `Player kicked for: ${reason}` });
        }

        // 18.6 Ban Player
        if (urlStr.includes('/player/ban')) {
            const urlObj = new URL(urlStr, window.location.origin);
            const license = urlObj.searchParams.get('license');
            const netid = urlObj.searchParams.get('netid');
            
            let foundPlayer = null;
            if (license && mockDetailedPlayers[license]) {
                foundPlayer = mockDetailedPlayers[license];
            } else if (netid) {
                foundPlayer = Object.values(mockDetailedPlayers).find(p => p.netid?.toString() === netid.toString());
            }
            
            let reason = 'No reason provided';
            let duration = 'permanent';
            if (init?.body) {
                try {
                    const parsedBody = JSON.parse(init.body as string);
                    reason = parsedBody.reason || 'No reason provided';
                    duration = parsedBody.duration || 'permanent';
                } catch (_) {}
            }
            
            if (foundPlayer) {
                foundPlayer.isConnected = false;
                foundPlayer.netid = undefined;
                foundPlayer.actionHistory = foundPlayer.actionHistory || [];
                
                let exp = undefined;
                if (duration !== 'permanent') {
                    const val = parseInt(duration);
                    if (duration.includes('day')) {
                        exp = Math.floor(Date.now() / 1000 + val * 86400);
                    } else if (duration.includes('hour')) {
                        exp = Math.floor(Date.now() / 1000 + val * 3600);
                    } else {
                        exp = Math.floor(Date.now() / 1000 + 86400 * 3); // fallback 3d
                    }
                }
                
                foundPlayer.actionHistory.unshift({
                    id: `B-${Math.floor(Math.random() * 9000 + 1000)}`,
                    type: 'ban',
                    author: 'dev_sc0pez',
                    reason: reason,
                    ts: Math.floor(Date.now() / 1000),
                    exp: exp
                });
            }
            return jsonResponse({ type: 'success', msg: 'Player banned successfully!' });
        }

        // 18.7 DM / Message Player
        if (urlStr.includes('/player/message')) {
            let message = '';
            if (init?.body) {
                try {
                    const parsedBody = JSON.parse(init.body as string);
                    message = parsedBody.message || '';
                } catch (_) {}
            }
            return jsonResponse({ type: 'success', msg: `Direct Message sent successfully: "${message}"` });
        }

        if (urlStr.includes('/player/search')) {
            const list = Object.values(mockDetailedPlayers).map(p => ({
                license: p.license,
                displayName: p.displayName,
                playTime: p.playTime,
                tsJoined: p.tsJoined,
                tsLastConnection: p.tsLastConnection || Math.floor(Date.now() / 1000),
                notes: p.notes,
                isAdmin: p.isAdmin || false,
                isOnline: p.isConnected,
                isWhitelisted: !!p.tsWhitelisted,
                profilePicture: p.profilePicture
            }));
            return jsonResponse({
                hasReachedEnd: true,
                players: list
            });
        }

        // Fallback for everything else
        try {
            return await originalFetch(input, init);
        } catch (_) {
            return jsonResponse({});
        }
    };
}



//If inside NUI, silence console.* calls to prevent confusion.
if (!window.txConsts.isWebInterface) {
    console.log = () => { };
    console.info = () => { };
    console.warn = () => { };
    console.error = () => { };
    console.debug = () => { };
    console.table = () => { };
    console.group = () => { };
    console.groupEnd = () => { };
    console.groupCollapsed = () => { };
    console.time = () => { };
    console.timeEnd = () => { };
    console.timeLog = () => { };
}

//Detecting if the user is on a mobile device
try {
    window.txIsMobile = isMobile({ tablet: true });
} catch (error) {
    window.txIsMobile = false;
}

//Detecting locale preferences
try {
    window.txBrowserLocale = window?.nuiSystemLanguages ?? navigator.language ?? 'en';
} catch (error) {
    window.txBrowserLocale = 'en';
}
try {
    const localeOption = Intl.DateTimeFormat(window.txBrowserLocale, { hour: 'numeric' }).resolvedOptions().hour12
    window.txBrowserHour12 = localeOption ?? true;
} catch (error) {
    window.txBrowserHour12 = true;
}

//If the initial routing is from WebPipe, remove it from the pathname so the router can handle it
if (window.location.pathname.substring(0, 8) === '/WebPipe') {
    console.info('Removing WebPipe prefix from the pathname.');
    const newUrl = window.location.pathname.substring(8) + window.location.search + window.location.hash;
    window.history.replaceState(null, '', newUrl);
}

//Rendering auth or main pages depending on if the user is authenticated
const authRoutePrefixes = ['/login', '/addMaster'];
const isAuthRoute = (pathname: string) => {
    const normalized = pathname.startsWith('/fivem') ? pathname.substring(6) : pathname;
    return authRoutePrefixes.some(prefix => normalized.startsWith(prefix));
}


export function AuthContextSwitch() {
    useAtomValue(logoutWatcher);
    useAtomValue(pageTitleWatcher);
    const isAuthenticated = useIsAuthenticated();

    useEffect(() => {
        if (isAuthenticated) {
            //Replace the current URL with the redirect path if it exists and is valid
            const urlParams = new URLSearchParams(window.location.search);
            const redirectPath = urlParams.get('r');
            if (redirectPath) {
                if (isValidRedirectPath(redirectPath)) {
                    window.history.replaceState(null, '', redirectPath);
                } else {
                    window.history.replaceState(null, '', '/fivem');
                }
            } else if (isAuthRoute(window.location.pathname)) {
                window.history.replaceState(null, '', '/fivem');
            }
        } else {
            //Unless the user is already in the auth pages, redirect to the login page
            if (!window.txConsts.hasMasterAccount && !window.location.pathname.startsWith('/fivem/addMaster')) {
                console.log('No master account detected. Redirecting to addMaster page.');
                window.history.replaceState(null, '', '/fivem/addMaster/pin');
            } else if (!isAuthRoute(window.location.pathname)) {
                console.log('User is not authenticated. Redirecting to login page.');
                redirectToLogin();
            }
        }
    }, [isAuthenticated]);

    return isAuthenticated ? <MainShell /> : <AuthShell />;
}


export function HubRouter() {
    return (
        <Switch>
            <Route path="/panel">
                <OwnerApp />
            </Route>
            <Route path="/panel/*">
                <OwnerApp />
            </Route>
            <Route path="/fivem/*">
                <Router base="/fivem">
                    <AuthContextSwitch />
                </Router>
            </Route>
            <Route path="/fivem">
                <Router base="/fivem">
                    <AuthContextSwitch />
                </Router>
            </Route>
            <Route path="/">
                <WelcomePortal />
            </Route>
            <Route>
                <WelcomePortal />
            </Route>
        </Switch>
    );
}


function DevHelperPanel() {
    const [isOpen, setIsOpen] = useState(false);
    const [ownerMode, setOwnerMode] = useState(
        localStorage.getItem('sclabs_owner_mode') === 'true'
    );
    const setAuthData = useSetAuthData();
    const setOwnerSecret = useSetAtom(writeSecretAtom);
    const setOwnerSession = useSetAtom(writeSessionAtom);
    const isAuthenticatedFiveM = useIsAuthenticated();

    useEffect(() => {
        const handleOwnerModeChange = () => {
            setOwnerMode(localStorage.getItem('sclabs_owner_mode') === 'true');
        };
        window.addEventListener('sclabs_owner_mode_change', handleOwnerModeChange);
        return () => window.removeEventListener('sclabs_owner_mode_change', handleOwnerModeChange);
    }, []);

    const handleBypassFiveM = () => {
        setAuthData({
            name: 'dev_sc0pez',
            permissions: ['all_permissions'],
            isMaster: true,
            isTempPassword: false,
            csrfToken: 'dev-csrf-token',
        });
        txToast.success('FiveM Auth Bypassed!');
    };

    const handleBypassOwner = () => {
        setOwnerSecret('mock-password');
        setOwnerSession('mock-session-dev');
        txToast.success('Owner Panel Auth Bypassed!');
    };

    const handleResetAll = () => {
        setAuthData(false);
        setOwnerSecret(null);
        setOwnerSession(null);
        txToast.info('All Auth Cleared.');
    };

    if (!ownerMode) return null;

    return (
        <div className="fixed bottom-4 right-4 z-[9999] font-mono text-left select-none">
            {/* Gear Button */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center justify-center w-12 h-12 rounded-full bg-[#00ffd1]/20 hover:bg-[#00ffd1]/30 border border-[#00ffd1]/50 text-[#00ffd1] shadow-[0_0_15px_rgba(0,255,209,0.3)] transition-all duration-300 hover:scale-105"
                title="Dev Helper Panel"
            >
                ⚙️
            </button>

            {/* Panel Drawer */}
            {isOpen && (
                <div className="absolute bottom-14 right-0 w-72 bg-[#0a0e17]/95 border border-[#00ffd1]/40 rounded-xl p-4 shadow-[0_10px_30px_rgba(0,0,0,0.8),0_0_20px_rgba(0,255,209,0.1)] backdrop-blur-md text-xs text-slate-300">
                    <div className="flex justify-between items-center border-b border-[#00ffd1]/20 pb-2 mb-3">
                        <span className="text-[#00ffd1] font-bold uppercase tracking-wider">🛠️ DEV HELP DECK</span>
                        <button onClick={() => setIsOpen(false)} className="text-slate-500 hover:text-slate-300">✖</button>
                    </div>

                    <div className="space-y-3">
                        <div>
                            <div className="text-[10px] uppercase text-slate-500 mb-1">Navigation Quick Jump</div>
                            <div className="grid grid-cols-3 gap-1">
                                <button onClick={() => window.location.href = '/'} className="bg-slate-800/60 hover:bg-slate-800 border border-white/5 py-1 px-1.5 rounded text-center text-white font-mono">Hub</button>
                                <button onClick={() => window.location.href = '/fivem'} className="bg-slate-800/60 hover:bg-slate-800 border border-white/5 py-1 px-1.5 rounded text-center text-white font-mono">FiveM</button>
                                <button onClick={() => window.location.href = '/panel'} className="bg-slate-800/60 hover:bg-slate-800 border border-white/5 py-1 px-1.5 rounded text-center text-white font-mono">Owner</button>
                            </div>
                        </div>

                        <div>
                            <div className="text-[10px] uppercase text-slate-500 mb-1">Bypass & Auth Cheats</div>
                            <div className="flex flex-col gap-1.5">
                                <button
                                    onClick={handleBypassFiveM}
                                    className="w-full text-left bg-[#00ffd1]/10 hover:bg-[#00ffd1]/20 border border-[#00ffd1]/30 py-1.5 px-2 rounded flex items-center justify-between text-[#00ffd1] font-semibold font-mono"
                                >
                                    <span>🔓 Bypass FiveM Login</span>
                                    <span>{isAuthenticatedFiveM ? '✅' : '⚡'}</span>
                                </button>
                                <button
                                    onClick={handleBypassOwner}
                                    className="w-full text-left bg-[#8b5cf6]/10 hover:bg-[#8b5cf6]/20 border border-[#8b5cf6]/30 py-1.5 px-2 rounded flex items-center justify-between text-[#a78bfa] font-semibold font-mono"
                                >
                                    <span>🔓 Bypass Owner Login</span>
                                    <span>⚡</span>
                                </button>
                                <button
                                    onClick={() => window.postMessage({ type: 'openAccountModal' }, '*')}
                                    className="w-full text-left bg-[#eab308]/10 hover:bg-[#eab308]/20 border border-[#eab308]/30 py-1.5 px-2 rounded flex items-center justify-between text-[#facc15] font-semibold font-mono"
                                >
                                    <span>👤 Open Account Dialog</span>
                                    <span>⚡</span>
                                </button>
                            </div>
                        </div>

                        <div className="border-t border-slate-800 pt-2.5">
                            <button
                                onClick={handleResetAll}
                                className="w-full bg-red-950/40 hover:bg-red-900/60 border border-red-500/30 text-red-400 py-1.5 px-2 rounded font-semibold text-center font-mono"
                            >
                                🔄 Reset & Clear Sessions
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

ReactDOM.createRoot(document.getElementById('root')!).render(
    <StrictMode>
        <ErrorBoundary FallbackComponent={AppErrorFallback}>
            <ThemeProvider>
                <HubRouter />
                {import.meta.env.DEV && <DevHelperPanel />}
            </ThemeProvider>
        </ErrorBoundary>
    </StrictMode>,
)
